import React, { useState } from 'react';
import Avatar from 's2s-avatar'; 
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { colorGrid } from 's2s-themes';
import { combinedSVGIcons }  from 's2s-svg-icons-combined';
import { getIntlText } from '../js/utilities';

const ContactListItemContainer = styled.div`
  align-items: center;
  background-color : ${colorGrid.gray0};
  box-shadow: inset 0 -1px 0 0 #d9dde5;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: 48px;
  :hover {
    background-color: rgba(36, 57, 145, 0.04);
  };
`;

const AvatarNameArea = styled.div`
  display: flex;
  flex: 3;
  font-size: 16px;
  padding-right: 5px;
`;

const AvatarContainer = styled.div`
  /* this needs to be adjusted for new avatar component w/ styled-components */
  align-items: center;
  background-color: ${colorGrid.gray0};
  border: 1px solid #e5e6e8;
  border-radius: 50%;
  box-sizing: border-box;
  display: flex;
  height: 32px;
  justify-content: center;
  margin-left: 16px;
  overflow: hidden;
  width: 32px;
  
  div {
    background-color: #f2f2f3;
    color: #4c5863;
    font-family: Poppins;
    font-weight: 400;
    line-height: 24px;
  }
`;

const ContactNameContainer = styled.div`
  align-items: left;
  display: flex;
  flex-direction: column;
  margin: 0px 12px;
`;

const ContactName = styled.div`
  align-items: center;
  color: ${(props) => props.isChecked ? colorGrid.blue6 : '#001221'};
  display: flex;
  flex: 1;
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 200px;
  white-space: nowrap;
`;

const ContactCompany = styled.div`
  align-items: center;
  color: #4c5863;
  display: flex;
  flex: 1;
  font-family: Open Sans;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 200px;
  white-space: nowrap;
`;

const ContactActionContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  height: 48px; /* set by design */
  justify-content: center;
  margin: 0px auto;
  width: 58px; /* set by design */

  :hover {
    background-color: ${colorGrid.gray4};
  }
`;

const actionSvgStyle = {
  fill: '#243991',
  width: '24px',
  height: '24px'
};

const actionSvgHoverStyle = {
  fill: '#243991',
  width: '24px',
  height: '24px'
};

const ContactListItem = (props) => {
  const [actionHover, setActionHover] = useState(false);

  const getContactName = () => {
    let contactName;
    if (props.contact.name && (props.contact.name.last || props.contact.name.first)) {
      let n = (props.contact.name.first ? props.contact.name.first : '');
      n = n + (n.length > 0 ? ' ' : '') + (props.contact.name.last ? props.contact.name.last : '');
      contactName = `${n}`;
    } else if (props.contact.emails && props.contact.emails.length > 0) {
      contactName = props.contact.emails[0].address;
    } else {
      contactName = getIntlText(props.intl, 'ENTITYLIST-NO-NAME-EMAIL', "No name or email");
    }
    return contactName;
  };

  const isCompanyExist = () => {
    return props.contact.work && Object.keys(props.contact.work).length !== 0;
  };

  const onContactActionHover = () => {
    if (!actionHover) {
      setActionHover(true);
    }
  };

  const onMouseOut = () => {
    if (actionHover === true ) {
      setActionHover(false);
    }
  };

  const contactActionSVGStyle = () => {
    let style;
    if (actionHover) {
      style = actionSvgHoverStyle;
    } else {
      style = actionSvgStyle;
    }

    return style;
  };

  const handleItemClick = () => {
    if (props.contact.uuid !== undefined) {
      props.cbContactOnClick(props.contact);
    } else {
      console.error('The uuid for this conact is undefined.', props.contact);
    }
  };

  const handleActionClick = (e) => {
    e.stopPropagation();
    if (props.contact.uuid !== undefined) {
      props.cbContactActionOnClick(props.contact);
    } else {
      console.error('The uuid for this conact is undefined.', props.contact);
    }
  };
  
  const activeOnKeyDown = (e) => {
    switch (e.keyCode) {
      case 32:
        handleItemClick();
        break;
      case 13:
        handleItemClick();
        break;
      default:
    }
  };

  const DynamicSVG = typeof combinedSVGIcons[props.contactActionSVG] !== "undefined" ? combinedSVGIcons[props.contactActionSVG] : combinedSVGIcons['SettingsIconSVG'];
  const avatarObject = {
    email: props.contact.emails ? props.contact.emails[0].address : undefined,
    firstname: props.contact.name.first,
    lastname: props.contact.name.last
  };

  return (
    <ContactListItemContainer
      className="contactListItem"
      onClick={handleItemClick}
      onKeyDown={activeOnKeyDown}
      role="listitem"
      tabIndex={0}
    >
      <AvatarNameArea className="leftControlArea" title="View Contact">
        <AvatarContainer className="avatarComponent">
          <Avatar entity={avatarObject} size="medium" />
        </AvatarContainer>
        <ContactNameContainer className="contactNameContainer">
          <ContactName className="contactName" isChecked={false}>
            {getContactName()}
          </ContactName>
          { isCompanyExist() &&
            <ContactCompany>
              {props.contact.work.company}
            </ContactCompany>       
          }
        </ContactNameContainer>
      </AvatarNameArea>
      <ContactActionContainer
        className="contactAction"
        onMouseEnter={onContactActionHover}
        onMouseLeave={onMouseOut}
        onClick={handleActionClick}
      >
        <div className="rightContentArea" title={props.actionTitle}>
          <DynamicSVG svgStyle={contactActionSVGStyle()} />
        </div>
      </ContactActionContainer>
    </ContactListItemContainer>
  );
};

ContactListItem.propTypes = {
  actionTitle: PropTypes.string,
  cbContactActionOnClick: PropTypes.func,
  cbContactOnClick: PropTypes.func,
  contact: PropTypes.shape({
    name: PropTypes.shape({
      first: PropTypes.string,
      middle: PropTypes.string,
      last: PropTypes.string,
      initals: PropTypes.string,
      suffix: PropTypes.string,
      first_yomi: PropTypes.string,
      last_yomi: PropTypes.string
    }),
    uuid: PropTypes.string,
    gender: PropTypes.string,
    govt_it: PropTypes.shape({
      type: PropTypes.string,
      number: PropTypes.string
    }),
    work: PropTypes.shape({
      company: PropTypes.string,
      id: PropTypes.string,
      department: PropTypes.string,
      title: PropTypes.string,
      manager: PropTypes.string
    }),
    addresses: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        street: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        country: PropTypes.string,
        postal_code: PropTypes.string,
        pobox: PropTypes.string
      })
    ),
    phone_numbers: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        number: PropTypes.string,
        preferred: PropTypes.bool
      })
    ),
    fax_numbers: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        number: PropTypes.string,
        preferred: PropTypes.bool
      })
    ),
    emails: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        address: PropTypes.string,
        preferred: PropTypes.bool
      })
    )
  }),
  contactActionSVG: PropTypes.string
};

ContactListItem.defaultProps = {
  actionTitle: 'Send Message',
  contact: {}
};

export default ContactListItem;
